import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarDays, CheckCircle2, LogIn, LogOut, X, Ban } from "lucide-react";
import { api } from "@/lib/api-client";
import { queryClient } from "@/lib/query-client";
import { useAppStore } from "@/store/app-store";
import { Button } from "@/components/ui/Button";
import { BookingDetailDialog, type InitialBookingAction } from "@/components/resource/booking-detail";
import type { Booking } from "@/types/domain";
import { useMarkNotificationRead, useNotifications } from "./useNotifications";

const AUDIT_TYPES = ["CHECKIN_OVERDUE", "CHECKIN_DUE", "CHECKOUT_DUE"];

// Night-audit prompt: when a booking's check-in / check-out has come due (the backend sweep raises the
// notification), this pops up in the property the user is working in with the actions that resolve it.
export function StayAuditPrompt() {
  const role = useAppStore((state) => state.user?.role);
  const activeProperty = useAppStore((state) => state.activeProperty);
  const notifications = useNotifications();
  const markRead = useMarkNotificationRead();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [opened, setOpened] = useState<{ booking: Booking; action: InitialBookingAction } | null>(null);
  const staleHandled = useRef<Set<string>>(new Set());

  const next = (notifications.data ?? []).find((item) => !item.isRead && item.bookingId && AUDIT_TYPES.includes(item.type) && !dismissed.has(item.id));
  const booking = useQuery({
    queryKey: ["audit-booking", next?.bookingId],
    queryFn: () => api<Booking>(`/bookings/${next!.bookingId}`),
    enabled: Boolean(next && activeProperty),
  });
  const cancelMutation = useMutation({
    mutationFn: (bookingId: string) => api<Booking>(`/bookings/${bookingId}/status`, { method: "PATCH", body: { status: "CANCELLED" } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["bookings"] });
      await queryClient.invalidateQueries({ queryKey: ["audit-booking"] });
    },
  });

  const expectedStatus = next?.type === "CHECKOUT_DUE" ? "CHECKED_IN" : "CONFIRMED";
  const stale = Boolean(next && booking.data && booking.data.bookingStatus !== expectedStatus);
  useEffect(() => {
    // Already handled elsewhere (checked in/out, cancelled): clear the notification instead of nagging.
    if (next && stale && !staleHandled.current.has(next.id)) {
      staleHandled.current.add(next.id);
      markRead.mutate(next.id);
    }
  }, [next, stale, markRead]);

  if (opened && role) {
    return <BookingDetailDialog booking={opened.booking} role={role === "ADMIN" ? "ADMIN" : "OWNER"} initialAction={opened.action} onClose={() => setOpened(null)} />;
  }
  if (!next || !booking.data || stale || !activeProperty || booking.data.stayProfileId !== activeProperty.id) return null;

  const current = booking.data;
  const open = (action: InitialBookingAction) => {
    markRead.mutate(next.id);
    setOpened({ booking: current, action });
  };

  return (
    <div className="live-booking-backdrop" role="presentation">
      <section className="live-booking-modal" role="dialog" aria-modal="true" aria-label={next.title}>
        <header>
          <span className="live-booking-timer"><CalendarDays size={16} /> {next.title}</span>
          <button className="icon-button" type="button" aria-label="Remind me later" onClick={() => setDismissed((set) => new Set(set).add(next.id))}><X /></button>
        </header>
        <div className="live-booking-main">
          <h2>{current.guestName}</h2>
          <p>{next.body}</p>
        </div>
        <footer style={{ flexWrap: "wrap" }}>
          {next.type === "CHECKIN_OVERDUE" && (
            <>
              <Button variant="secondary" loading={cancelMutation.isPending} onClick={() => { if (window.confirm(`Cancel booking ${current.bookingRef}?`)) { markRead.mutate(next.id); cancelMutation.mutate(current.id); } }}><Ban size={16} /> Cancel booking</Button>
              <Button onClick={() => open("change-dates")}><CalendarDays size={16} /> Change stay date</Button>
            </>
          )}
          {next.type === "CHECKIN_DUE" && <Button onClick={() => open("check-in")}><LogIn size={16} /> Check in</Button>}
          {next.type === "CHECKOUT_DUE" && (
            <>
              <Button variant="secondary" onClick={() => open("extend")}><CheckCircle2 size={16} /> Extend stay</Button>
              <Button onClick={() => open("check-out")}><LogOut size={16} /> Check out</Button>
            </>
          )}
        </footer>
        {cancelMutation.error && <div className="form-error">{cancelMutation.error.message}</div>}
      </section>
    </div>
  );
}
